import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { nailists } from "../../mocks/nailists";
import { menuItems } from "../../mocks/menu";

const BOOKING_URL = "https://readdy.ai/api/form/d6uvddd1l1ubpb129a6g";

const timeSlots = [
  "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00"
];

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const nailistParam = searchParams.get("nailist");
  const initialNailist = nailistParam ? parseInt(nailistParam, 10) : 0;

  const [selectedNailist, setSelectedNailist] = useState<number>(initialNailist);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    menu: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (nailistParam) {
      setSelectedNailist(parseInt(nailistParam, 10));
    }
  }, [nailistParam]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "notes" && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.date || !formData.time || !formData.menu) {
      setError("必須項目をすべてご入力ください");
      return;
    }
    setError("");
    setSubmitting(true);

    const selectedNailistName = selectedNailist === 0
      ? "指名なし"
      : nailists.find((n) => n.id === selectedNailist)?.name ?? "指名なし";

    const body = new URLSearchParams();
    body.append("name", formData.name);
    body.append("email", formData.email);
    body.append("phone", formData.phone);
    body.append("date", formData.date);
    body.append("time", formData.time);
    body.append("menu", formData.menu);
    body.append("nailist", selectedNailistName);
    body.append("notes", formData.notes);

    try {
      await fetch(BOOKING_URL, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF8F6]">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="bg-white rounded-2xl border border-[#EDE7DF] p-12 max-w-md w-full text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto bg-[#F2E8DF] rounded-full mb-6">
              <i className="ri-check-line text-[#B8956A] text-2xl" />
            </div>
            <h2 className="text-2xl text-[#3D2E2A] mb-3" style={{ fontFamily: "'Noto Serif JP', serif", fontWeight: 400 }}>
              ご予約を受け付けました
            </h2>
            <p className="text-[#8B7355] text-sm leading-relaxed mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              ご入力いただいたメールアドレス宛に確認メールをお送りいたします。2〜3営業日以内にスタッフよりご連絡いたします。
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#B8956A] text-white px-8 py-3 rounded-full text-sm tracking-wider cursor-pointer whitespace-nowrap hover:bg-[#A07B55] transition-colors"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              ホームに戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F6]">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-16 text-center bg-[#FAF8F6]">
        <p className="text-[#B8956A] text-xs tracking-[0.3em] uppercase mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>Reservation</p>
        <h1 className="text-4xl lg:text-5xl text-[#3D2E2A] mb-4" style={{ fontFamily: "'Noto Serif JP', serif", fontWeight: 300 }}>
          ご予約
        </h1>
        <p className="text-[#8B7355] text-sm" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
          ご希望のネイリストをお選びいただき、ご予約フォームにお進みください
        </p>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        {/* Nailist Selection */}
        <section className="mb-10">
          <h2 className="text-lg text-[#3D2E2A] mb-6" style={{ fontFamily: "'Noto Serif JP', serif", fontWeight: 400 }}>
            担当ネイリストを選ぶ
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* No Preference */}
            <button
              onClick={() => setSelectedNailist(0)}
              className={`p-5 rounded-xl border-2 cursor-pointer text-center transition-all ${
                selectedNailist === 0
                  ? "border-[#B8956A] bg-[#FDF6EF]"
                  : "border-[#EDE7DF] bg-white hover:border-[#C9A86A]"
              }`}
            >
              <div className="w-16 h-16 flex items-center justify-center mx-auto bg-[#F2E8DF] rounded-full mb-3">
                <i className="ri-team-line text-[#B8956A] text-2xl" />
              </div>
              <p className="text-[#3D2E2A] text-sm font-medium mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>指名なし</p>
              <p className="text-[#A8937A] text-xs" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>おまかせ</p>
            </button>

            {nailists.map((n) => (
              <button
                key={n.id}
                onClick={() => setSelectedNailist(n.id)}
                className={`p-5 rounded-xl border-2 cursor-pointer text-center transition-all ${
                  selectedNailist === n.id
                    ? "border-[#B8956A] bg-[#FDF6EF]"
                    : "border-[#EDE7DF] bg-white hover:border-[#C9A86A]"
                }`}
              >
                <div className="w-16 h-16 mx-auto rounded-full overflow-hidden mb-3">
                  <img src={n.image} alt={n.name} className="w-full h-full object-cover object-top" />
                </div>
                <p className="text-[#3D2E2A] text-sm font-medium mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>{n.name}</p>
                <p className="text-[#A8937A] text-xs" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{n.specialty}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Booking Form */}
        <section className="bg-white rounded-2xl border border-[#EDE7DF] p-8 lg:p-10">
          <h2 className="text-lg text-[#3D2E2A] mb-8" style={{ fontFamily: "'Noto Serif JP', serif", fontWeight: 400 }}>
            予約情報を入力する
          </h2>

          <form data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-[#6B5548] text-xs mb-2 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  お名前 <span className="text-[#B8956A]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="山田 花子"
                  required
                  className="w-full border border-[#EDE7DF] rounded-lg px-4 py-3 text-sm text-[#3D2E2A] placeholder-[#C5B9AF] outline-none focus:border-[#B8956A] transition-colors"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[#6B5548] text-xs mb-2 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  電話番号 <span className="text-[#B8956A]">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="090-XXXX-XXXX"
                  required
                  className="w-full border border-[#EDE7DF] rounded-lg px-4 py-3 text-sm text-[#3D2E2A] placeholder-[#C5B9AF] outline-none focus:border-[#B8956A] transition-colors"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-[#6B5548] text-xs mb-2 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  メールアドレス <span className="text-[#B8956A]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="hanako@example.com"
                  required
                  className="w-full border border-[#EDE7DF] rounded-lg px-4 py-3 text-sm text-[#3D2E2A] placeholder-[#C5B9AF] outline-none focus:border-[#B8956A] transition-colors"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>

              {/* Menu */}
              <div>
                <label className="block text-[#6B5548] text-xs mb-2 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  ご希望メニュー <span className="text-[#B8956A]">*</span>
                </label>
                <select
                  name="menu"
                  value={formData.menu}
                  onChange={handleChange}
                  required
                  className="w-full border border-[#EDE7DF] rounded-lg px-4 py-3 text-sm text-[#3D2E2A] outline-none focus:border-[#B8956A] transition-colors cursor-pointer bg-white"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  <option value="">選択してください</option>
                  {menuItems.map((m) => (
                    <option key={m.id} value={m.name}>{m.name}（{m.price}）</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-[#6B5548] text-xs mb-2 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  ご希望日 <span className="text-[#B8956A]">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full border border-[#EDE7DF] rounded-lg px-4 py-3 text-sm text-[#3D2E2A] outline-none focus:border-[#B8956A] transition-colors cursor-pointer"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-[#6B5548] text-xs mb-2 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  ご希望時間 <span className="text-[#B8956A]">*</span>
                </label>
                <select
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className="w-full border border-[#EDE7DF] rounded-lg px-4 py-3 text-sm text-[#3D2E2A] outline-none focus:border-[#B8956A] transition-colors cursor-pointer bg-white"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  <option value="">選択してください</option>
                  {timeSlots.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[#6B5548] text-xs mb-2 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                ご要望・備考（任意）
                <span className="text-[#A8937A] ml-2">{formData.notes.length}/500</span>
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="デザインのご要望、アレルギーなどがあればご記入ください"
                maxLength={500}
                rows={4}
                className="w-full border border-[#EDE7DF] rounded-lg px-4 py-3 text-sm text-[#3D2E2A] placeholder-[#C5B9AF] outline-none focus:border-[#B8956A] transition-colors resize-none"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              />
            </div>

            {/* Selected Nailist Display */}
            <div className="bg-[#FAF8F6] rounded-lg px-5 py-4 border border-[#EDE7DF] flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center">
                <i className="ri-user-heart-line text-[#B8956A] text-lg" />
              </span>
              <div>
                <p className="text-[#6B5548] text-xs mb-0.5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>担当ネイリスト</p>
                <p className="text-[#3D2E2A] text-sm font-medium" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {selectedNailist === 0
                    ? "指名なし（おまかせ）"
                    : nailists.find((n) => n.id === selectedNailist)?.name ?? "指名なし"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedNailist(0)}
                className="ml-auto text-xs text-[#A8937A] hover:text-[#B8956A] cursor-pointer whitespace-nowrap transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                変更する
              </button>
            </div>

            {error && (
              <p className="text-red-500 text-xs text-center" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{error}</p>
            )}

            <div className="text-center pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#B8956A] text-white px-16 py-4 rounded-full text-sm tracking-widest hover:bg-[#A07B55] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line animate-spin text-sm" /></span>
                    送信中...
                  </>
                ) : (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-check-line text-sm" /></span>
                    予約を申し込む
                  </>
                )}
              </button>
              <p className="text-[#A8937A] text-xs mt-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                ご予約は仮受付となります。スタッフより確認のご連絡をいたします。
              </p>
            </div>
          </form>
        </section>
      </div>

      <Footer />
    </div>
  );
}
